import type { FormKitNode } from '@formkit/core';
import { extend } from '@formkit/utils';
import { datepicker, type FormKitProInput } from '@formkit-enterprise/pro';
import dayjs from '@/helpers/dayjs';
import i18next from '@/helpers/i18n';

// The Pro datepicker has no week numbers, so the calendar rows and the picked date get them through schema extensions.
const weekSections = {
  // Keeps the Pro genesis datepicker styles, which select on the data-type.
  outer: { attrs: { 'data-type': 'datepicker', 'data-week-numbers': true } },
  // Pro pins the popover below the input at the input's width, so it overflows near the viewport's edges.
  panelWrapper: {
    attrs: {
      style: {
        if: '$usePopover',
        then: '$fns.popoverStyle($popoverCoordinates, $popoverMeasured)',
      },
    },
  },
  weekDays: { attrs: { 'data-week': '' } },
  weekDay: { children: '$fns.weekdayLabel($day)' },
  week: { attrs: { 'data-week': '$fns.weekNumber($week.0)' } },
  dayCell: {
    attrs: {
      'data-today': '$fns.isToday($day)',
      class: '$classes.dayCell + " " + $fns.dayClass($day)',
    },
  },
  suffix: {
    $el: 'span',
    if: '$_value',
    key: 'week-suffix',
    children: '$fns.weekSuffix($_value)',
  },
};

// ISO weeks match the Monday-first rows, so a Sunday gets the same number as its row.
const isoWeek = (date: Date | string) => dayjs(date).isoWeek();

// Pro drops values that don't match `valueFormat`, while forms often hold full timestamps or Dates.
// Both resolve to the local calendar date, as the old picker did.
function toDateOnly(value: unknown) {
  const isTimestamp =
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value);
  if (value instanceof Date || isTimestamp) {
    return dayjs(value).format('YYYY-MM-DD');
  }
  return value;
}

const VIEWPORT_GAP = 8;

// Opens below the input, or above it when only that side fits, and stays inside the viewport.
function popoverStyle(node: FormKitNode) {
  const id = node.props.id;
  const anchor = document.getElementById(`${id}_inner`);
  const panel = document.getElementById(`${id}_popover`);
  if (!anchor) return { margin: 0 };

  const rect = anchor.getBoundingClientRect();
  const height = panel?.offsetHeight ?? 0;
  const width = panel?.offsetWidth ?? 0;
  const spaceBelow = window.innerHeight - rect.bottom;
  const openAbove = height > spaceBelow && rect.top > spaceBelow;
  const top = openAbove ? rect.top - height : rect.bottom;
  const clamp = (value: number, max: number) =>
    Math.max(VIEWPORT_GAP, Math.min(value, max - VIEWPORT_GAP));

  return {
    position: 'fixed',
    margin: 0,
    inset: 'auto',
    width: 'max-content',
    minWidth: `${rect.width}px`,
    top: `${clamp(top, window.innerHeight - height)}px`,
    left: `${clamp(rect.left, window.innerWidth - width)}px`,
  };
}

function weekNumbers(node: FormKitNode) {
  node.addProps(['getClasses', 'popoverMeasured']);
  // The panel's size is only known after it renders, and changes with the month's row count or the panel shown.
  const remeasure = () => {
    if (!node.props.expanded) return;
    node.props.popoverMeasured = false;
    requestAnimationFrame(() => {
      node.props.popoverMeasured = true;
    });
  };
  node.on('prop:expanded', remeasure);
  node.on('prop:renderedDate', remeasure);
  node.on('prop:panel', remeasure);
  // FormKit Pro (as of 0.130) listens for resize on `document`, where it never fires; drop this once it listens on `window`.
  const stopResize = () => window.removeEventListener('resize', remeasure);
  node.on('prop:expanded', ({ payload }) => {
    if (!payload) return stopResize();
    window.addEventListener('resize', remeasure);
  });
  node.on('destroyed', stopResize);
  node.hook.input((value, next) => {
    // Pro reports an empty field as undefined, which would change a form's '' or null and mark it dirty.
    const stillEmpty =
      value == null && (node._value === '' || node._value === null);
    return next(stillEmpty ? node._value : toDateOnly(value));
  });
  node.props.valueFormat ??= 'YYYY-MM-DD';
  node.props.valueLocale ??= 'en';
  node.props.format ??= 'D MMM YYYY';
  node.props.weekStart ??= 1;
  // The global FormKit config always provides an `en` locale, so only an explicit prop should win over the app language.
  node.addProps(['locale']);
  if (!('locale' in node.props)) {
    node.props.locale = i18next.resolvedLanguage || 'en';
  }
  node.props.clearable ??= true;
  node.props.placeholder ??= i18next.t(
    'formulate:datePicker.defaultPlaceholder'
  );

  node.on('created', () => {
    if (!node.context) return;
    node.context.fns.weekNumber = isoWeek;
    // The arguments only make the style re-render when Pro moves the panel and once it has a size.
    node.context.fns.popoverStyle = () => popoverStyle(node);
    node.context.fns.dayClass = (day: Date) =>
      node.props.getClasses?.(day) ?? '';
    node.context.fns.weekdayLabel = (day: Date) =>
      dayjs(day).locale(node.props.locale).format('dd');
    node.context.fns.isToday = (day: Date) => dayjs(day).isSame(dayjs(), 'day');
    node.context.fns.weekSuffix = (date: string) =>
      i18next.t('formulate:datePicker.weekSuffix', {
        weekNum: isoWeek(date),
      });
  });
}

export const weekDatepicker: FormKitProInput = {
  ...datepicker,
  // Pro inputs build their schema from the license key, so this wraps that factory rather than the schema itself.
  schema: (key) => (extensions) =>
    datepicker.schema(key)(
      extend(weekSections, extensions ?? {}) as typeof extensions
    ),
  features: [weekNumbers, ...(datepicker.features || [])],
};
